import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../Style/ReserveOccaission.css";
import NavBar from "../Components/NavBar.js";
import Footer from "../Components/Footer.js";
import GoogleLocation from "../Components/GoogleLocation.js";
import LocationIcon from "../icons/LocationIcon.js";
import SingleDatePicker from "../Components/SingleDatePicker.js";
import CustomDropdown from "../Components/StatusDropdown.js";
// import { ReactComponent as OrnamentIcon } from "../icons/OrnamentIcon.svg";
// import BalloonIcon from "../icons/BalloonIcon.js";
// import { ReactComponent as ClockIcon } from "../icons/ClockIcon.svg";
// import reserveHero from "../assets/WhatsApp Image 2026-06-18 at 23.12.57 (2).jpeg";
// import { ReactComponent as CustomCreationIcon } from "../icons/CustomCreationIcon.svg";
import { api } from "../api/client.js";
import {
  getApiFieldErrors,
  getRequestErrorMessage,
  toIsoDate,
  validateReservationForm,
} from "../Utils/formValidation.js";

const OCCASIONS = [
  "Private Celebration",
  "Wedding",
  "Corporate Gathering",
  "Hospitality or Venue",
  "Gift or Custom Sculpture",
  "Something Else",
];

// const JOURNEY_NOTES = [
//   {
//     Icon: CustomCreationIcon,
//     title: "Tell us your vision",
//     text: "Share the occasion, the feeling, and the details that matter to you.",
//   },
//   {
//     Icon: LocationIcon,
//     title: "Choose your location",
//     text: "Enter an address or select it easily with our location picker.",
//   },
//   {
//     Icon: ClockIcon,
//     title: "We will be in touch",
//     text: "Our atelier will contact you to thoughtfully shape what comes next.",
//   },
// ];

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  address: "",
  occasion: "",
  date: "",
  message: "",
};

const compactAddressParts = (parts) =>
  parts.map((part) => String(part || "").trim()).filter(Boolean);

const formatLocationAddress = (location) => {
  const rawAddress = location.rawAddress || {};
  const streetAddress = compactAddressParts([
    rawAddress.house_number,
    rawAddress.road || location.road,
  ]).join(" ");
  const fallbackStreet = compactAddressParts([
    rawAddress.suburb || location.suburb,
    rawAddress.neighbourhood,
    rawAddress.city_district,
  ]).join(", ");
  const city =
    location.city ||
    rawAddress.city ||
    rawAddress.town ||
    rawAddress.village ||
    rawAddress.municipality;
  const state = location.state || rawAddress.state;
  const postal = location.postal || rawAddress.postcode;
  const country = location.country || rawAddress.country;
  const stateAndPostal = compactAddressParts([state, postal]).join(" ");
  const address = compactAddressParts([
    streetAddress || fallbackStreet,
    city,
    stateAndPostal,
    country,
  ]).join(", ");

  return address || "";
};

function ReserveOccaission() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [searchParams] = useSearchParams();
  const productSlug = useMemo(() => {
    const value = searchParams.get("product")?.trim().toLowerCase() || "";
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) ? value : "";
  }, [searchParams]);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const handleChange = ({ target: { name, value } }) => {
    setSubmitted(false);
    setSubmitError("");
    setFieldErrors((current) => ({ ...current, [name]: "" }));
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleLocationSelect = (location) => {
    const address = formatLocationAddress(location);

    setSelectedLocation(location);
    setFormData((current) => ({
      ...current,
      address: address || current.address,
    }));
    setFieldErrors((current) => ({ ...current, address: "" }));
    setIsLocationOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const validationErrors = validateReservationForm(formData);
    setFieldErrors(validationErrors);
    setSubmitError("");
    if (Object.keys(validationErrors).length > 0) return;

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      address: formData.address.trim(),
      ...(formData.occasion ? { occasion: formData.occasion } : {}),
      ...(formData.date ? { preferredDate: toIsoDate(formData.date) } : {}),
      ...(formData.message.trim() ? { message: formData.message.trim() } : {}),
      ...(selectedLocation
        ? {
            location: {
              lat: selectedLocation.lat,
              lng: selectedLocation.lng,
              ...(selectedLocation.label
                ? { label: selectedLocation.label }
                : {}),
            },
          }
        : {}),
      ...(productSlug ? { productSlug } : {}),
    };

    setSubmitting(true);
    try {
      await api.reservations.create(payload);
      setSubmittedName(formData.name.trim());
      setFormData(INITIAL_FORM);
      setSelectedLocation(null);
      setFieldErrors({});
      setSubmitted(true);
    } catch (error) {
      const apiErrors = getApiFieldErrors(error);
      if (apiErrors.preferredDate) {
        apiErrors.date = apiErrors.preferredDate;
        delete apiErrors.preferredDate;
      }
      setFieldErrors(apiErrors);
      setSubmitError(
        Object.keys(apiErrors).length > 0
          ? "Please correct the highlighted fields and try again."
          : getRequestErrorMessage(
              error,
              "We could not send your reservation. Please check your connection and try again.",
            ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reserve-page">
      <NavBar />

      <main>
        {/* <section className="reserve-hero">
          <img
            className="reserve-hero-image"
            src={reserveHero}
            alt="Hot air balloon floating above Paris"
          />
          <div className="reserve-hero-overlay" aria-hidden="true" />
          <div className="reserve-hero-content">
            <span className="reserve-hero-icon" aria-hidden="true">
              <BalloonIcon />
            </span>
            <p>Begin your journey</p>
            <h1>Reserve an Occasion</h1>
            <span className="reserve-hero-ornament" aria-hidden="true">
              <OrnamentIcon />
            </span>
            <p className="reserve-hero-copy">
              Every meaningful experience begins with a conversation. Tell us
              where your imagination is taking you.
            </p>
          </div>
        </section> */}

        {/* <aside className="reserve-intro">
            <p className="reserve-eyebrow">A thoughtful beginning</p>
            <h2>Let us create something that feels entirely yours.</h2>
            <p className="reserve-intro-copy">
              Share a few details below and our atelier will help bring your
              occasion to life with warmth, beauty, and intention.
            </p>

            <div className="reserve-journey-notes">
              {JOURNEY_NOTES.map(({ Icon, title, text }, index) => (
                <article className="reserve-journey-note" key={title}>
                  <span className="reserve-note-number">0{index + 1}</span>
                  <span className="reserve-note-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                </article>
              ))}
            </div>

            <blockquote>
              “The most beautiful occasions begin with the feeling you want
              people to remember.”
            </blockquote>
          </aside> */}

        <div className="reserve-form-card">
          <header className="reserve-form-header">
            <p>Reservation details</p>
            <h2>Tell us about your occasion</h2>
            {productSlug && (
              <p className="reserve-product-context">
                Selected piece: <strong>{productSlug}</strong>
              </p>
            )}
          </header>

          {submitted ? (
            <div className="reserve-success" role="status">
              <span aria-hidden="true">&#10022;</span>
              <h3>Your story is ready for its next chapter.</h3>
              <p>
                Thank you, {submittedName}. Your reservation request has been
                received will be in touch soon.
              </p>
              <button
                type="button"
                onClick={() => {
                  setFormData(INITIAL_FORM);
                  setSelectedLocation(null);
                  setSubmittedName("");
                  setSubmitted(false);
                }}
              >
                Create another request
              </button>
            </div>
          ) : (
            <form className="reserve-form" onSubmit={handleSubmit} noValidate>
              <div className="reserve-form-row">
                <label className="reserve-field">
                  <span>Your name *</span>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full name"
                    autoComplete="name"
                    required
                    aria-invalid={Boolean(fieldErrors.name)}
                    aria-describedby={
                      fieldErrors.name ? "reservation-name-error" : undefined
                    }
                  />
                  {fieldErrors.name && (
                    <small
                      id="reservation-name-error"
                      className="form-field-error"
                    >
                      {fieldErrors.name}
                    </small>
                  )}
                </label>

                <label className="reserve-field">
                  <span>Email address *</span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    aria-invalid={Boolean(fieldErrors.email)}
                    aria-describedby={
                      fieldErrors.email ? "reservation-email-error" : undefined
                    }
                  />
                  {fieldErrors.email && (
                    <small
                      id="reservation-email-error"
                      className="form-field-error"
                    >
                      {fieldErrors.email}
                    </small>
                  )}
                </label>
              </div>

              <div className="reserve-form-row">
                <label className="reserve-field">
                  <span>Phone number *</span>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+961 00 000 000"
                    autoComplete="tel"
                    required
                    aria-invalid={Boolean(fieldErrors.phone)}
                    aria-describedby={
                      fieldErrors.phone ? "reservation-phone-error" : undefined
                    }
                  />
                  {fieldErrors.phone && (
                    <small
                      id="reservation-phone-error"
                      className="form-field-error"
                    >
                      {fieldErrors.phone}
                    </small>
                  )}
                </label>

                <label className="reserve-field">
                  <span>Occasion</span>
                  <CustomDropdown
                    value={formData.occasion}
                    options={OCCASIONS}
                    placeholder="Select an occasion"
                    onChange={(occasion) => {
                      setSubmitted(false);
                      setFormData((current) => ({
                        ...current,
                        occasion,
                      }));
                      setFieldErrors((current) => ({
                        ...current,
                        occasion: "",
                      }));
                    }}
                  />
                </label>
              </div>

              <label className="reserve-field">
                <span>Preferred date</span>
                <SingleDatePicker
                  value={formData.date}
                  onChange={(date) => {
                    setFormData((current) => ({ ...current, date }));
                    setFieldErrors((current) => ({
                      ...current,
                      date: "",
                    }));
                  }}
                  minDate={new Date()}
                />
                {fieldErrors.date && (
                  <small
                    id="reservation-date-error"
                    className="form-field-error"
                  >
                    {fieldErrors.date}
                  </small>
                )}
              </label>

              <div className="reserve-address-block">
                <div className="reserve-address-heading">
                  <div>
                    <span className="reserve-address-icon" aria-hidden="true">
                      <LocationIcon />
                    </span>
                    <div>
                      <h3>Occasion address *</h3>
                      <p>Type it manually or place a pin on the map.</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setIsLocationOpen(true)}>
                    <LocationIcon />
                    Choose on map
                  </button>
                </div>

                <label className="reserve-field reserve-address-field">
                  <span className="reserve-visually-hidden">
                    Occasion address
                  </span>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Street, building, city, postal code, country"
                    autoComplete="street-address"
                    required
                    aria-invalid={Boolean(fieldErrors.address)}
                    aria-describedby={
                      fieldErrors.address
                        ? "reservation-address-error"
                        : undefined
                    }
                  />
                  {fieldErrors.address && (
                    <small
                      id="reservation-address-error"
                      className="form-field-error"
                    >
                      {fieldErrors.address}
                    </small>
                  )}
                </label>

                {selectedLocation && (
                  <p className="reserve-location-confirmed">
                    <span aria-hidden="true">&#10003;</span>
                    Location selected · {selectedLocation.lat.toFixed(5)},{" "}
                    {selectedLocation.lng.toFixed(5)}
                  </p>
                )}
              </div>

              <label className="reserve-field">
                <span>Anything else you would like us to know?</span>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your vision, preferred atmosphere, or any special details…"
                />
                {fieldErrors.message && (
                  <small
                    id="reservation-message-error"
                    className="form-field-error"
                  >
                    {fieldErrors.message}
                  </small>
                )}
              </label>

              {submitError && (
                <p className="form-submit-error" role="alert">
                  {submitError}
                </p>
              )}
              <p className="reserve-privacy">
                By sending this request, you agree that we may contact you about
                your occasion.
              </p>

              <button
                className="reserve-submit"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Send reservation request"}
                {!submitting && <span aria-hidden="true">&rarr;</span>}
              </button>
            </form>
          )}
        </div>
      </main>

      {isLocationOpen && (
        <div
          className="reserve-location-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsLocationOpen(false);
            }
          }}
        >
          <section
            className="reserve-location-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reserve-location-title"
          >
            <header>
              <div>
                <p>Delivery location</p>
                <h2 id="reserve-location-title">Where should we find you?</h2>
              </div>
              <button
                type="button"
                onClick={() => setIsLocationOpen(false)}
                aria-label="Close location picker"
              >
                &times;
              </button>
            </header>
            <GoogleLocation
              apiKey={googleMapsApiKey}
              initialValue={selectedLocation}
              onSelect={handleLocationSelect}
              onClose={() => setIsLocationOpen(false)}
            />
          </section>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default ReserveOccaission;
